import {
  apiUrl,
  apiOptions,
  handleError,
  handleResponse,
  getStatsData,
  getList,
  get,
  getSingleRecord,
  saveRecord,
  getAll,
  postAction,
  deleteAction,
  getAuditData,
  putAction,
  exportData,
} from './apiUtils';
import type { ApiListParamsWithUnderscores, ApiExportParams } from '../types/api/listParams';
import type { ListApiResponse } from '../types/apiResponse';
import type { Stats } from '../types/stats';

const baseUrl = apiUrl + '/users/';

export function getUserById(id: string): Promise<unknown> {
  return getSingleRecord(baseUrl, id);
}

export function getByUsername(username: string): Promise<unknown> {
  const url = `${baseUrl}authenticated`;
  return fetch(url, apiOptions()).then(handleResponse).catch(handleError);
}

export function getStats(id: string): Promise<Stats> {
  return getStatsData(baseUrl, id);
}

export function getStatsByDay(id: string): Promise<unknown> {
  return get(`${baseUrl}${id}/statsByDay/`);
}

export function getAggregateStats(
  id: string,
  from: Date,
  to: Date
): Promise<unknown> {
  const fromStr = from.toISOString().split('T')[0];
  const toStr = to.toISOString().split('T')[0];
  return get(
    `${baseUrl}${id}/aggregateStats?fromDate=${fromStr}&toDate=${toStr}`
  );
}

export function getAuthorisedStatsByDay(id: string): Promise<unknown> {
  return get(`${baseUrl}${id}/statsAuthorisedByDay/`);
}

export function getUploadedStatsByDay(id: string): Promise<unknown> {
  return get(`${baseUrl}${id}/statsUploadedByDay/`);
}

export function getAudit(id: string): Promise<unknown> {
  return getAuditData(baseUrl, id);
}

export function addAudit(id: string, comment: string): Promise<unknown> {
  return postAction(`${baseUrl}${id}/audit`, { comment });
}

export function getGeneralStats(): Promise<unknown> {
  return get(`${baseUrl}stats`);
}

export function getUsers(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}search`, params);
}

export function getUsersForGroup(
  userProfileGroupById: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}group/${userProfileGroupById}`, params);
}

export function saveUser(user: {
  creditLimit?: string | number;
  [key: string]: unknown;
}): Promise<unknown> {
  if (user.creditLimit) {
    user.creditLimit = parseFloat(String(user.creditLimit).replace(/,/g, ''));
  } else {
    user.creditLimit = undefined;
  }
  return saveRecord(baseUrl, user);
}

export function getAssignableUsers(): Promise<unknown> {
  return fetch(baseUrl + 'assignableusers', apiOptions())
    .then(handleResponse)
    .catch(handleError);
}

export function search(query: string): Promise<unknown> {
  return fetch(`${baseUrl}search/${query}`, apiOptions())
    .then(handleResponse)
    .catch(handleError);
}

export function getUserProfileGroupsForUser(
  userProfileId: string
): Promise<unknown> {
  return getAll(`${baseUrl}${userProfileId}/groups`);
}

export function getTerritoriesForUser(
  userProfileId: string
): Promise<unknown> {
  return getAll(`${baseUrl}${userProfileId}/territories`);
}

export function addTerritory(
  userProfileId: string,
  territoryId: string
): Promise<unknown> {
  return postAction(`${baseUrl}${userProfileId}/territory/${territoryId}`);
}

export function editUserTerritories(
  territoryIds: string[],
  userProfileId: string
): Promise<unknown> {
  return postAction(`${baseUrl}${userProfileId}/territories`, {
    territoryIds,
  });
}

export function removeTerritory(
  userProfileId: string,
  territoryId: string
): Promise<unknown> {
  return deleteAction(`${baseUrl}${userProfileId}/territory/${territoryId}`);
}

export function archiveUser(id: string): Promise<unknown> {
  return deleteAction(`${baseUrl}${id}`);
}

export function resendInvite(id: string): Promise<unknown> {
  return putAction(`${baseUrl}${id}/resendInvite`);
}

export function resetAuthenticator(id: string): Promise<unknown> {
  return putAction(`${baseUrl}${id}/resetAuthenticator`);
}

export function exportUserData(params: ApiExportParams): Promise<void> {
  return exportData(`${apiUrl}/export/users/search`, 'fullName', params);
}

export function setLanguage(language: string): Promise<unknown> {
  return putAction(`${baseUrl}setLanguage/${language}`);
}
